import type { ReactNode } from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import { useEstateOptions } from '@/api/estate-options';
import { Button } from '@/components/new-ui/button';
import { Checkbox } from '@/components/new-ui/checkbox';
import { DateField } from '@/components/new-ui/date-field';
import { Input } from '@/components/new-ui/input';
import { Label } from '@/components/new-ui/label';
import { OptionSelect } from '@/components/new-ui/option-select';
import { Separator } from '@/components/new-ui/separator';
import type { SelectOption } from '@/components/ui/select';
import { useProjects } from '@/features/projects/api/projects';
import { cn } from '@/lib/utils';

import type { EstatesFiltersSchemaType } from '../schemas';
import { hasActiveFilterValue } from './estate-filter-active-field';
import { useEstateFilterDateValidation } from './estate-filter-dates';

/** Border of a filter that currently has a value */
const activeClassName = 'border-primary';

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="space-y-3">
    <h3 className="font-semibold text-foreground">{title}</h3>
    {children}
  </div>
);

type RangeFieldProps = {
  name: 'buyingPrices' | 'rooms' | 'area';
  title: string;
  minPlaceholder: string;
  maxPlaceholder: string;
};

const RANGE_FIELD_NAMES = {
  buyingPrices: { min: 'buyingPrices.min', max: 'buyingPrices.max' },
  rooms: { min: 'rooms.min', max: 'rooms.max' },
  area: { min: 'minLivingArea', max: 'maxLivingArea' },
} as const;

// Range values are numbers like the filter schema, an empty input clears the value
const toRangeValue = (value: string) => {
  if (value.trim() === '') return undefined;
  const number = Number(value);
  return Number.isNaN(number) ? undefined : number;
};

const RangeField = ({ name, title, minPlaceholder, maxPlaceholder }: RangeFieldProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<EstatesFiltersSchemaType>();
  const names = RANGE_FIELD_NAMES[name];

  return (
    <Section title={title}>
      <div className="grid grid-cols-2 gap-4">
        {(
          [
            ['min', names.min, minPlaceholder, t('min', 'Min')],
            ['max', names.max, maxPlaceholder, t('max', 'Max')],
          ] as const
        ).map(([key, fieldName, placeholder, label]) => (
          <Controller
            key={key}
            control={control}
            name={fieldName}
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor={`estate-filter-${fieldName}`} className="text-muted-foreground">
                  {label}
                </Label>
                <Input
                  {...field}
                  id={`estate-filter-${fieldName}`}
                  type="number"
                  min={0}
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(toRangeValue(event.target.value))}
                  placeholder={placeholder}
                  className={cn(hasActiveFilterValue(field.value) && activeClassName)}
                />
              </div>
            )}
          />
        ))}
      </div>
    </Section>
  );
};

type SelectFilterProps = {
  name:
    | 'projectId'
    | 'marketType'
    | 'usageType'
    | 'propertyType'
    | 'heatingType'
    | 'creatorId'
    | 'flooring'
    | 'glazing';
  title: string;
  placeholder: string;
  options: SelectOption[];
  disabled?: boolean;
};

const SelectFilter = ({ name, title, placeholder, options, disabled }: SelectFilterProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<EstatesFiltersSchemaType>();

  return (
    <Section title={title}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <OptionSelect
            ref={field.ref}
            id={`estate-filter-${name}`}
            name={field.name}
            value={field.value === undefined ? undefined : String(field.value)}
            onChange={(next) => {
              // The select works with strings, store the option id with its own type
              const option = options.find((item) => String(item.id) === next);
              field.onChange(option ? option.id : next);
            }}
            onBlur={field.onBlur}
            options={options}
            placeholder={placeholder}
            resetLabel={t('all', 'All')}
            disabled={disabled}
            className={cn(hasActiveFilterValue(field.value) && activeClassName)}
          />
        )}
      />
    </Section>
  );
};

type DateFilterProps = {
  name: 'createdAt.min' | 'createdAt.max' | 'updatedAt.min' | 'updatedAt.max';
  label: string;
};

const DateFilter = ({ name, label }: DateFilterProps) => {
  const { t } = useTranslation();
  const { control, clearErrors, setError } = useFormContext<EstatesFiltersSchemaType>();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const date = field.value ? new Date(field.value) : undefined;

        return (
          <div className="min-w-0 space-y-1.5">
            <Label htmlFor={`estate-filter-${name}`} className="text-muted-foreground">
              {label}
            </Label>
            <div className="relative">
              <DateField
                id={`estate-filter-${name}`}
                ariaLabel={label}
                value={date}
                invalid={Boolean(error)}
                className={cn(date && !error && activeClassName, date && 'pr-9')}
                onChange={(next) => field.onChange(next)}
                // A typed date that is not valid yet keeps the last value, so the URL is not updated
                onValidityChange={(isValid) => {
                  if (isValid) {
                    clearErrors(name);
                    return;
                  }
                  setError(name, {
                    type: 'manual',
                    message: t('invalidDateFormat', {
                      // The format as the placeholder spells it (TT.MM.JJJJ in German)
                      value: t('date-type'),
                      interpolation: { escapeValue: false },
                    }),
                  });
                }}
              />
              {date ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                  aria-label={t('clearDate')}
                  onClick={() => {
                    field.onChange(undefined);
                    clearErrors(name);
                  }}
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              ) : null}
            </div>
          </div>
        );
      }}
    />
  );
};

type DateRangeFilterProps = {
  name: 'createdAt' | 'updatedAt';
  title: string;
};

/** From and to dates of one range, with the range or format message under both fields */
const DateRangeFilter = ({ name, title }: DateRangeFilterProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<EstatesFiltersSchemaType>();
  const { errors } = useFormState({ control, name });
  const message = errors[name]?.min?.message || errors[name]?.max?.message;

  return (
    <Section title={title}>
      <div className="grid grid-cols-2 gap-4">
        <DateFilter name={`${name}.min`} label={t('from', 'From')} />
        <DateFilter name={`${name}.max`} label={t('to', 'To')} />
      </div>
      {message ? (
        <p role="alert" className="text-xs text-destructive">
          {message}
        </p>
      ) : null}
    </Section>
  );
};

const ADDITIONAL_FEATURES = [
  'loggia',
  'balcony',
  'garden',
  'parkingSpace',
  'basement',
  'elevator',
] as const;

type EstateFilterNewUiProps = {
  creatorList?: SelectOption[];
  filterOwn?: boolean;
  /** Hides the area and rooms ranges */
  hideLivingAreaAndRooms?: boolean;
  hasFilters: boolean;
  onClear?: () => void;
};

/** Filter sections of the estate filter drawer in the layout of the redesign, same fields and values */
export const EstateFilterNewUi = ({
  creatorList,
  filterOwn,
  hideLivingAreaAndRooms = false,
  hasFilters,
  onClear,
}: EstateFilterNewUiProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<EstatesFiltersSchemaType>();
  const { data: estateOptions } = useEstateOptions();
  const { data: projects } = useProjects({ query: { filters: { hasEstates: true } } });

  useEstateFilterDateValidation();

  const projectOptions: SelectOption[] =
    projects?.map((project) => ({
      title: project.name || `Project_${project.id}`,
      id: project.id,
    })) || [];

  return (
    <div className="space-y-6">
      {hasFilters && onClear ? (
        <Button type="button" variant="outline" className="w-full" onClick={onClear}>
          <X />
          {t('clearAllFilters', 'Clear All Filters')}
        </Button>
      ) : null}
      <Separator />
      <RangeField
        name="buyingPrices"
        title={t('priceRange', 'Price Range (€)')}
        minPlaceholder="0"
        maxPlaceholder="1,000,000"
      />
      {hideLivingAreaAndRooms ? null : (
        <>
          <Separator />
          <RangeField
            name="area"
            title={t('areaRange', 'Area Range (m²)')}
            minPlaceholder="0"
            maxPlaceholder="500"
          />
          <Separator />
          <RangeField
            name="rooms"
            title={t('numberOfRooms', 'Number of Rooms')}
            minPlaceholder="0"
            maxPlaceholder="10"
          />
        </>
      )}
      <Separator />
      <SelectFilter
        name="projectId"
        title={t('project')}
        placeholder={t('selectProject')}
        options={projectOptions}
      />
      <Separator />
      <SelectFilter
        name="marketType"
        title={t('marketingType')}
        placeholder={t('selectMarketingType')}
        options={estateOptions?.marketingType || []}
      />
      <Separator />
      <SelectFilter
        name="usageType"
        title={t('usage')}
        placeholder={t('selectUsageType')}
        options={estateOptions?.usage || []}
      />
      <Separator />
      <SelectFilter
        name="propertyType"
        title={t('propertyType')}
        placeholder={t('selectPropertyType')}
        options={estateOptions?.propertyType || []}
      />
      <Separator />
      <SelectFilter
        name="heatingType"
        title={t('heatingType')}
        placeholder={t('selectHeatingType')}
        options={estateOptions?.heatingType || []}
      />
      {creatorList ? (
        <>
          <Separator />
          <SelectFilter
            name="creatorId"
            title={t('author')}
            placeholder={t('selectCreator')}
            options={creatorList}
            disabled={filterOwn}
          />
        </>
      ) : null}
      <Separator />
      <SelectFilter
        name="flooring"
        title={t('flooring')}
        placeholder={t('selectFlooring')}
        options={estateOptions?.flooring || []}
      />
      <Separator />
      <SelectFilter
        name="glazing"
        title={t('glazing')}
        placeholder={t('selectGlazing')}
        options={estateOptions?.glazing || []}
      />
      <Separator />
      <DateRangeFilter name="createdAt" title={t('createdDate', 'Created Date')} />
      <Separator />
      <DateRangeFilter name="updatedAt" title={t('updatedDate', 'Updated Date')} />
      <Separator />
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 pb-2">
        {ADDITIONAL_FEATURES.map((fieldName) => (
          <Controller
            key={fieldName}
            control={control}
            name={fieldName}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`estate-filter-${fieldName}`}
                  ref={field.ref}
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) => field.onChange(checked)}
                  onBlur={field.onBlur}
                />
                <Label htmlFor={`estate-filter-${fieldName}`} className="font-normal">
                  {t(fieldName)}
                </Label>
              </div>
            )}
          />
        ))}
      </div>
    </div>
  );
};
